import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, FileText, X, Search, ExternalLink, Loader2, Lock } from 'lucide-react';
import type { Protocol } from '../../types';
import api from '../../../services/api';

interface ProtocolSelectorProps {
  protocols: Protocol[];
  selectedProtocol: Protocol | null;
  onSelect: (protocol: Protocol | null) => void;
  disabled?: boolean;
  /** 'bar' renders its own bordered row (desktop); 'inline' renders just the
      chip + actions for embedding in the mobile chat header. */
  variant?: 'bar' | 'inline';
  isMobile?: boolean;
}

export function ProtocolSelector({
  protocols,
  selectedProtocol,
  onSelect,
  disabled = false,
  variant = 'bar',
  isMobile = false,
}: ProtocolSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoadingProtocol, setIsLoadingProtocol] = useState(false);
  const [protocolError, setProtocolError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close on outside click / Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Focus search when the picker opens on desktop. On the phone sheet,
  // auto-focus would pop the keyboard over half the list — let the user
  // reach for search if they need it.
  useEffect(() => {
    if (isOpen && !isMobile && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    if (!isOpen) setSearchTerm('');
  }, [isOpen, isMobile]);

  const filteredProtocols = protocols.filter(protocol => {
    const search = searchTerm.toLowerCase();
    return (
      protocol.protocol_number.toLowerCase().includes(search) ||
      protocol.sponsor_name?.toLowerCase().includes(search) ||
      protocol.study_nickname?.toLowerCase().includes(search)
    );
  });

  const handleSelect = (protocol: Protocol) => {
    onSelect(protocol);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(null);
  };

  const handleViewProtocol = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!selectedProtocol || isLoadingProtocol) return;

    setIsLoadingProtocol(true);
    setProtocolError(null);

    try {
      // Try fetching protocol-type documents first
      let response = await api.get(
        `/api/intelligence/protocols/${selectedProtocol.id}/documents?document_type=protocol`
      );
      let docs = response.data;

      // Fallback: fetch all documents if no protocol-type docs found
      if (!docs || docs.length === 0) {
        response = await api.get(
          `/api/intelligence/protocols/${selectedProtocol.id}/documents`
        );
        docs = response.data;
      }

      if (!docs || docs.length === 0) {
        setProtocolError('No documents found for this protocol');
        setTimeout(() => setProtocolError(null), 4000);
        return;
      }

      // Download the first document as a blob
      const docId = docs[0].id;
      const downloadResponse = await api.get(
        `/api/intelligence/documents/${docId}/download`,
        { responseType: 'blob' }
      );

      const blob = new Blob([downloadResponse.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (err: any) {
      console.error('Error viewing protocol document:', err);
      setProtocolError(err.response?.data?.error || 'Failed to load protocol document');
      setTimeout(() => setProtocolError(null), 4000);
    } finally {
      setIsLoadingProtocol(false);
    }
  }, [selectedProtocol, isLoadingProtocol]);

  const protocolList = (
    <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
      {filteredProtocols.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
          {searchTerm ? 'No protocols match your search' : 'No protocols available'}
        </div>
      ) : (
        filteredProtocols.map((protocol) => (
          <button
            key={protocol.id}
            onClick={() => handleSelect(protocol)}
            className={`w-full min-h-[44px] px-4 py-2.5 text-left hover:bg-primary-50 transition-colors duration-150 border-b border-gray-50 last:border-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 dark:hover:bg-primary-900/20 dark:border-gray-700/50 ${
              selectedProtocol?.id === protocol.id ? 'bg-primary-50 dark:bg-primary-900/20' : ''
            }`}
          >
            <div className="font-medium text-gray-900 text-sm line-clamp-1 dark:text-gray-100">
              {protocol.study_nickname || protocol.protocol_number}
            </div>
            <div className="text-xs text-gray-500 mt-0.5 dark:text-gray-400">
              {protocol.study_nickname && <span>{protocol.protocol_number}</span>}
              {protocol.study_nickname && protocol.sponsor_name && <span> &bull; </span>}
              {protocol.sponsor_name && <span>{protocol.sponsor_name}</span>}
            </div>
            {protocol.site_name && (
              <div className="text-xs text-primary-600 mt-0.5 dark:text-primary-400">
                {protocol.site_name}
              </div>
            )}
          </button>
        ))
      )}
    </div>
  );

  const searchBox = (
    <div className="relative">
      <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
      {/* text-base on the phone: iOS zooms the page on focus of any input under 16px */}
      <input
        ref={searchInputRef}
        type="text"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search protocols..."
        className="w-full pl-8 pr-3 py-2 text-base sm:py-1.5 sm:text-sm border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
      />
    </div>
  );

  const lockHint = (
    <p className="text-xs text-gray-500 dark:text-gray-400">
      Protocol is locked for the duration of the chat. Start a new chat to change it.
    </p>
  );

  const picker = isMobile ? (
    // Bottom sheet
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/50" onClick={() => setIsOpen(false)} />
      <div className="absolute inset-x-0 bottom-0 flex max-h-[75dvh] flex-col rounded-t-2xl bg-white pb-[env(safe-area-inset-bottom)] shadow-xl dark:bg-gray-800">
        <div className="mx-auto mt-2 h-1 w-9 flex-shrink-0 rounded-full bg-gray-300 dark:bg-gray-600" />
        <div className="flex flex-shrink-0 items-center justify-between pl-4 pr-2 pt-1.5 pb-0.5">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Select a protocol</h2>
          <button
            onClick={() => setIsOpen(false)}
            className="flex h-11 w-11 items-center justify-center rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        <div className="flex-shrink-0 px-4 pb-2">{searchBox}</div>
        {protocolList}
        <div className="flex-shrink-0 border-t border-gray-100 px-4 py-2.5 dark:border-gray-700">
          {lockHint}
        </div>
      </div>
    </div>
  ) : (
    // Anchored dropdown (desktop)
    <div className="absolute left-0 right-0 top-full z-50 mt-1.5 w-full max-w-md overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:bg-gray-800 dark:border-gray-700">
      <div className="p-2 border-b border-gray-100 dark:border-gray-700">{searchBox}</div>
      <div className="flex max-h-64 flex-col">{protocolList}</div>
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 dark:bg-gray-700/50 dark:border-gray-700">
        {lockHint}
      </div>
    </div>
  );

  const trigger = (
    <button
      type="button"
      onClick={() => !disabled && setIsOpen(!isOpen)}
      title={disabled ? 'Protocol is locked for this chat — start a new chat to change it' : undefined}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      className={`flex min-h-[44px] min-w-0 flex-1 items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 text-left text-sm transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-600 dark:bg-gray-700/50 md:min-h-0 md:py-1.5 ${
        disabled
          ? 'cursor-default'
          : 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700'
      }`}
    >
      <FileText
        size={15}
        className={`flex-shrink-0 ${selectedProtocol ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`}
      />
      {selectedProtocol ? (
        <span className="min-w-0 flex-1 truncate">
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {selectedProtocol.protocol_number}
          </span>
          {selectedProtocol.sponsor_name && (
            <span className="ml-1.5 text-gray-500 dark:text-gray-400">
              {selectedProtocol.sponsor_name}
            </span>
          )}
        </span>
      ) : (
        <span className="min-w-0 flex-1 truncate text-gray-500 dark:text-gray-400">
          Select a protocol
        </span>
      )}
      {selectedProtocol && !disabled && (
        <span
          role="button"
          tabIndex={0}
          onClick={handleClear}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') handleClear(e as any);
          }}
          className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors duration-150 hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-600 dark:hover:text-gray-300"
          title="Clear selection"
          aria-label="Clear protocol selection"
        >
          <X size={14} />
        </span>
      )}
      {disabled ? (
        <Lock size={13} className="flex-shrink-0 text-gray-400" aria-label="Protocol locked for this chat" />
      ) : (
        <ChevronDown
          size={15}
          className={`flex-shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      )}
    </button>
  );

  const viewButton = selectedProtocol && (
    <button
      onClick={handleViewProtocol}
      disabled={isLoadingProtocol}
      className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-primary-600 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:bg-primary-900/40 md:h-auto md:w-auto md:gap-1 md:px-2 md:py-1.5 md:text-xs md:font-medium"
      title="View protocol document"
      aria-label="View protocol document"
    >
      {isLoadingProtocol ? (
        <Loader2 size={15} className="animate-spin" />
      ) : (
        <ExternalLink size={15} />
      )}
      <span className="hidden md:inline">View</span>
    </button>
  );

  const errorNote = protocolError && (
    <div className="absolute left-0 right-0 top-full z-40 mt-1 rounded-md bg-red-50 px-3 py-1.5 text-xs text-red-600 shadow-sm dark:bg-red-900/40 dark:text-red-300">
      {protocolError}
    </div>
  );

  if (variant === 'inline') {
    return (
      <div ref={containerRef} className="relative flex min-w-0 flex-1 items-center gap-0.5">
        {trigger}
        {viewButton}
        {errorNote}
        {isOpen && !disabled && picker}
      </div>
    );
  }

  return (
    <div className="flex-shrink-0 border-b border-gray-200 bg-white px-4 py-2 dark:border-gray-700 dark:bg-gray-800" ref={containerRef}>
      <div className="relative flex items-center gap-1.5">
        <span className="mr-1 hidden flex-shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400 md:inline">
          Protocol
        </span>
        {trigger}
        {viewButton}
        {errorNote}
        {isOpen && !disabled && picker}
      </div>
    </div>
  );
}
